import axios from 'axios';
import { logger } from '../help/utils/logger';
import { useAuthStore } from '@/features/auth/stores/auth.store';

// Get API URL from environment variables, fallback to a default if not sets
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Generate or retrieve a stable sessionId for this browser session/tab
function getOrCreateSessionId(): string {
  try {
    const existing = localStorage.getItem('sessionId');
    if (existing && existing.length > 0) return existing;
    const id = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2,10)}-${Math.random().toString(16).slice(2,10)}`;
    localStorage.setItem('sessionId', id);
    return id;
  } catch {
    // Fallback when storage not available
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2,10)}`;
  }
}

const SESSION_ID = getOrCreateSessionId();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-session-id': SESSION_ID,
  },
});

// Add a request interceptor
api.interceptors.request.use(
  async (config) => {
    // Ensure session id header is present on every request
    config.headers['x-session-id'] = SESSION_ID;

    // Check if we're in the application form context (URL contains /application/)
    if (window.location.pathname.includes('/application/')) {
      // Get shortId from URL - it's the first part after /application/
      const shortId = window.location.pathname.split('/application/')[1]?.split('/')[0];

      if (shortId) {
        // Add shortId as a custom header instead of using Authorization
        config.headers['X-Application-Short-ID'] = shortId;
        logger.debug('API:Request', 'Added shortId to request headers', { shortId });
        return config;
      }
    }

    // For all other requests (dashboard context), get Firebase ID token from authStore
    const firebaseIdToken = await useAuthStore.getState().getFirebaseIdToken();
    if (firebaseIdToken) {
      config.headers.Authorization = `Bearer ${firebaseIdToken}`;
    } else {
      // Ensure Authorization header is removed if no token exists
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor just for logging
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Just return the rejected promise, no need to log every error
    return Promise.reject(error);
  }
);

export default api;
